import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, BookOpen, Trophy, ArrowRight } from 'lucide-react';
import { Exam, ExamResult } from '@/types/exam';

interface ExamDashboardProps {
  onSelectExam: (exam: Exam) => void;
}

export default function ExamDashboard({ onSelectExam }: ExamDashboardProps) {
  // Mock data - replace with real data from backend
  const upcomingExams: Exam[] = [
    {
      id: '1',
      name: 'Mathematics Midterm',
      subject: 'Mathematics',
      class: 'JSS1',
      totalMarks: 100,
      duration: 60,
      startDate: new Date('2025-12-10'),
      endDate: new Date('2025-12-10'),
      status: 'published',
      questions: [],
    },
    {
      id: '2',
      name: 'English Language Quiz',
      subject: 'English',
      class: 'JSS1',
      totalMarks: 50,
      duration: 30,
      startDate: new Date('2025-12-10'),
      endDate: new Date('2025-12-10'),
      status: 'published',
      questions: [],
    },
  ];

  const recentResults: ExamResult[] = [
    {
      id: '1',
      examId: '1',
      studentId: 'STU001',
      studentName: 'Akande Johnson',
      totalMarks: 100,
      marksObtained: 82,
      percentage: 82,
      grade: 'A',
      answers: [],
      startTime: new Date('2025-12-03'),
      endTime: new Date('2025-12-03'),
      duration: 60,
      position: 2,
    },
    {
      id: '2',
      examId: '2',
      studentId: 'STU001',
      studentName: 'Akande Johnson',
      totalMarks: 50,
      marksObtained: 45,
      percentage: 90,
      grade: 'A',
      answers: [],
      startTime: new Date('2025-12-02'),
      endTime: new Date('2025-12-02'),
      duration: 30,
      position: 1,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-primary">Examinations</h1>
        <p className="text-muted-foreground">Take exams and track your performance</p>
      </div>

      {/* Upcoming Exams */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-primary">Upcoming Exams</h2>
          <Badge variant="outline" className="bg-accent/10">
            {upcomingExams.length} Available
          </Badge>
        </div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {upcomingExams.map((exam) => (
            <motion.div key={exam.id} variants={itemVariants}>
              <Card className="h-full flex flex-col hover:shadow-lg transition-shadow duration-300 border-border hover:border-secondary">
                <div className="p-6 flex-1 space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-primary">
                          {exam.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">{exam.subject}</p>
                      </div>
                      <Badge className="bg-secondary/10 text-secondary hover:bg-secondary/20">
                        {exam.class}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-3 pt-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>{exam.duration} minutes</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <BookOpen className="w-4 h-4" />
                      <span>{exam.totalMarks} marks</span>
                    </div>
                  </div>

                  <div className="bg-primary/5 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Total Marks</p>
                    <p className="text-2xl font-bold text-primary">{exam.totalMarks}</p>
                  </div>
                </div>

                <div className="p-6 border-t border-border">
                  <Button
                    onClick={() => onSelectExam(exam)}
                    className="w-full bg-primary hover:bg-primary/90 gap-2"
                  >
                    Start Exam <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Recent Results */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-primary">Recent Results</h2>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-3"
        >
          {recentResults.map((result) => (
            <motion.div key={result.id} variants={itemVariants}>
              <Card className="p-4 border-border hover:border-secondary transition-all duration-300">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-primary">{result.studentName}</h3>
                    <p className="text-sm text-muted-foreground">
                      {new Date(result.startTime).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Score</p>
                      <p className="text-2xl font-bold text-primary">
                        {result.marksObtained}/{result.totalMarks}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Percentage</p>
                      <p className="text-2xl font-bold text-secondary">
                        {result.percentage}%
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Position</p>
                      <div className="flex items-center gap-1">
                        <Trophy className="w-4 h-4 text-accent" />
                        <p className="text-2xl font-bold text-accent">
                          {result.position}
                        </p>
                      </div>
                    </div>

                    <Badge
                      className={`text-white ${
                        result.percentage >= 80
                          ? 'bg-green-600'
                          : result.percentage >= 60
                          ? 'bg-blue-600'
                          : 'bg-red-600'
                      }`}
                    >
                      {result.grade}
                    </Badge>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
